/**
 * Streaming guard for markdown whose meaning depends on a line that has not fully arrived.
 *
 * markdown-it decides some block types from the next line: `Title\n---` is a heading and
 * `| A | B |` is only a table once its divider row exists. Showing text in the middle of those
 * lines renders a block that changes type a moment later. This returns how much of `text` can
 * be shown without that ambiguity; the caller holds back the rest until more text arrives.
 */

/** A line with only list, heading, quote, rule, setext or table markers and no content yet. */
const MARKER_ONLY_LINE = /^[\s\-*_=+#>|:.)\d]*$/

function isPendingLine(line: string): boolean {
  return line !== '' && (line.includes('|') || MARKER_ONLY_LINE.test(line))
}

export function getStableMarkdownLength(text: string): number {
  const tailStart = text.lastIndexOf('\n') + 1
  const tail = text.slice(tailStart)
  const tailPending = isPendingLine(tail)

  if (tailStart === 0) {
    return tailPending ? 0 : text.length
  }

  // The last complete line may be a table header whose divider row has not arrived yet.
  const lastLineStart = text.lastIndexOf('\n', tailStart - 2) + 1
  const lastLine = text.slice(lastLineStart, tailStart - 1)
  if ((tail === '' || tailPending) && lastLine.includes('|')) {
    const previousLine =
      lastLineStart === 0
        ? ''
        : text.slice(text.lastIndexOf('\n', lastLineStart - 2) + 1, lastLineStart - 1)
    if (!previousLine.includes('|')) {
      return lastLineStart
    }
  }

  return tailPending ? tailStart : text.length
}
